import { Component, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseFormComponent } from 'src/app/widgets/global/BaseFormComponent';
import { MediaService } from 'src/app/services/media.service';
import { FormGroup, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { WhiteSpacesValidator } from 'src/app/validators/whitespaces-validator'
import { ImageGuidelinesComponent } from 'src/app/pages/image-guidelines/image-guidelines.component';
import { MatDialog } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Guid } from "guid-typescript";
import { MonitoringService } from 'src/app/services/monitoring.service';
import { ToastrService } from 'ngx-toastr';

export interface Image {
  url: string;
  description: string;
  descriptionName: string;
  titleName: string;
  title: string;
  id: number;
}

@Component({
  selector: 'app-media-form',
  templateUrl: './images-form.component.html',
  styleUrls: ['./images-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ImagesFormComponent),
    multi: true
  }]
})
export class ImagesFormComponent extends BaseFormComponent {

  uploadedMedia: Image[] = [];
  pendingCount = 0;
  isDragOver = false;
  maxImages = 6;
  maxFileSize = 2 * 1024 * 1024; // 2MB
  allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

  constructor(
    private mediaService: MediaService,
    private whiteSpacesValidator: WhiteSpacesValidator,
    public dialog: MatDialog,
    private monitoringService: MonitoringService,
    private toastr: ToastrService
  ) {
    super();
    this.form = new FormGroup({
      atLeastOneImage: new FormControl(null, Validators.required)
    });
  }

  openImageGuidelines() {
    const dialogRef = this.dialog.open(ImageGuidelinesComponent);
    dialogRef.afterClosed().subscribe((result: string) => {
      console.log(`Dialog closed: ${result}`);
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(Array.from(files));
    }
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(Array.from(input.files));
      input.value = ''; // Reset so same file can be selected again
    }
  }

  private processFiles(files: File[]) {
    const remainingSlots = this.maxImages - this.uploadedMedia.length;
    const filesToProcess = files.slice(0, remainingSlots);

    for (const file of filesToProcess) {
      if (!this.validateFile(file)) {
        continue;
      }
      this.uploadFile(file);
    }
  }

  private validateFile(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.allowedExtensions.includes(ext)) {
      this.toastr.error(`File "${file.name}" has an unsupported format. Use JPG, PNG, GIF or WebP.`);
      this.monitoringService.logEvent('Image Error', { fileName: file.name, error: 'Invalid extension' });
      return false;
    }
    if (file.size > this.maxFileSize) {
      this.toastr.error(`File "${file.name}" exceeds the 2MB size limit.`);
      this.monitoringService.logEvent('Image Error', { fileName: file.name, error: 'File too large' });
      return false;
    }
    return true;
  }

  private async uploadFile(file: File) {
    this.pendingCount++;

    try {
      const blobDetails = await this.mediaService.uploadMedia(file);

      if (blobDetails) {
        const titleControlName = `imagetitle${Guid.create()}`;
        const descriptionControlName = `imagedescription${Guid.create()}`;

        const titleControl = new FormControl('', [
          Validators.required,
          this.whiteSpacesValidator.validate(),
          Validators.minLength(25),
          Validators.maxLength(50)
        ]);
        this.form.addControl(titleControlName, titleControl);
        titleControl.markAsDirty();

        const descriptionControl = new FormControl('', [
          Validators.required,
          this.whiteSpacesValidator.validate(),
          Validators.minLength(25),
          Validators.maxLength(240)
        ]);
        this.form.addControl(descriptionControlName, descriptionControl);
        descriptionControl.markAsDirty();

        this.uploadedMedia.push({
          url: blobDetails.url,
          title: '',
          description: '',
          id: this.uploadedMedia.length + 1,
          titleName: titleControlName,
          descriptionName: descriptionControlName
        });

        (this.value).images = this.uploadedMedia;
        this.updateImageRequiredControl();
      }
    } catch (ex) {
      this.toastr.error(`Failed to upload "${file.name}". Please try again.`);
      console.error('Upload failed:', ex);
    } finally {
      this.pendingCount--;
    }
  }

  async removeImage(index: number) {
    const image = this.uploadedMedia[index];

    // Remove form controls
    this.form.removeControl(image.titleName);
    this.form.removeControl(image.descriptionName);

    // Remove from array
    this.uploadedMedia.splice(index, 1);
    (this.value).images = this.uploadedMedia;
    this.updateImageRequiredControl();
  }

  makePrimary(index: number) {
    const [image] = this.uploadedMedia.splice(index, 1);
    this.uploadedMedia.unshift(image);
    (this.value).images = this.uploadedMedia;
  }

  private updateImageRequiredControl() {
    const ctrl = this.form.get('atLeastOneImage');
    if (this.uploadedMedia.length > 0) {
      ctrl.setValue('uploaded');
    } else {
      ctrl.setValue(null);
    }
    ctrl.markAsTouched();
  }
}